import React, { useEffect, useState, forwardRef } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Box,
    Tooltip as MuiTooltip,
    TableContainer,
    Divider
} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import Ordersdetails from '../comp/OrdersDetails.jsx'


export const OrderReportPage = forwardRef(({ reportRange, rawData }, ref) => {
    const theme = useTheme();

    const [chartData, setChartData] = useState([]);
    const [topItems, setTopItems] = useState([]);
    const [orderSummary, setOrderSummary] = useState({
        total: 0,
        dineIn: 0,
        takeOut: 0,
        kiosk: 0,      
        online: 0,     
        other: 0,      
        voided: 0,
        completed: 0,
    });
    // State to hold all completed orders for Ordersdetails
    const [allCompletedOrders, setAllCompletedOrders] = useState([]);
    
    // Helper function to map order type code to name
    const getOrderTypeName = (typeCode) => {
        switch (typeCode) {
            case 0: return 'Dine In (In-store)';
            case 1: return 'Take Out (Pickup)';
            case 2: return 'Kiosk';
            case 3: return 'Online';
            case 4: return 'Drive-Thru';
            case 5: return '3rd Party Delivery';
            default: return 'Other';
        }
    };


    useEffect(() => {
        // --- DATA SOURCING ---
        let ordersArray = rawData?.orders || []; // Assuming rawData is { dailyData: [...], orders: [...] }

        // Load from sessionStorage if prop is empty (for new tab view)
        if (ordersArray.length === 0) {
            const stored = sessionStorage.getItem("orderReportData");
            console.log("Order Report Page: ", stored);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    // FIX: Assuming the storage payload has the flat order list directly under 'orders'
                    ordersArray = parsed.rawData?.orders || parsed.rawData || []; 
                } catch (err) {
                    console.error("Failed to parse stored order report data:", err);
                    ordersArray = [];
                }
            }
        }
        
        if (ordersArray.length === 0) {
             setChartData([]);
             setTopItems([]);
             setOrderSummary({ total: 0, dineIn: 0, takeOut: 0, kiosk: 0, online: 0, other: 0, voided: 0, completed: 0 });
             setAllCompletedOrders([]);
             return;
        }

        // --- 1. Chart Data & Summary Counters ---
        const hourlyMap = {};
        let summary = { total: 0, dineIn: 0, takeOut: 0, kiosk: 0, online: 0, other: 0, voided: 0, completed: 0 };
        const itemMap = {};
        let combinedCompletedOrders = [];

        ordersArray.forEach(order => {
            
            // Check for voided orders (oStatus === 5)
            if (order.oStatus === 5) {
                summary.voided += 1;
                summary.total += 1;
                return; // Skip remaining processing for voided orders
            }
            
            // Process COMPLETED Orders (oStatus === 2)
            if (order.oStatus === 2) {
                summary.completed += 1;
                summary.total += 1;
                combinedCompletedOrders.push(order); // Add to the list for Ordersdetails
                
                // Hourly Sales Trend (Counting Orders per Hour)
                if (order.startTime) {
                    try {
                        const date = new Date(order.startTime);
                        if (!isNaN(date.getTime())) {
                            const hour = date.getHours(); // 0-23
                             hourlyMap[hour] = (hourlyMap[hour] || 0) + 1;
                        }
                    } catch(e) { console.error("Error parsing order startTime:", order.startTime, e); }
                 }
                
                // Detailed Order Type Count
                switch (order.type) {
                    case 0: summary.dineIn++; break;
                    case 1: summary.takeOut++; break;
                    case 2: summary.kiosk++; break;
                    case 3: summary.online++; break; 
                    default: summary.other++; break;
                }

                // Top Items Calculation
                order.orders?.forEach(subOrder => {
                    subOrder.order?.forEach(item => {
                        itemMap[item.item] = (itemMap[item.item] || 0) + (item.quantity ?? 0);
                    });
                });
            }
        });

        // Finalize chart data (sort by hour and convert to string label)
        const chart = Object.entries(hourlyMap)
            .map(([hour, count]) => ({ hour: parseInt(hour), orders: count })) // Keep hour as number for sorting
            .sort((a, b) => a.hour - b.hour) // Sort numerically
            .map(item => ({ hour: `${item.hour.toString().padStart(2, "0")}:00`, orders: item.orders })); // Convert hour to string label


        // Finalize top items
        const topItemsList = Object.entries(itemMap)
            .map(([item, qty]) => ({ item, qty }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 10); // Get top 10

        setChartData(chart);
        setOrderSummary(summary);
        setTopItems(topItemsList);
        setAllCompletedOrders(combinedCompletedOrders); // Set combined orders for detail view

    }, [reportRange, rawData]);

    // Format top selling items table data
    const formattedTopItems = topItems.map(row => (
        <TableRow key={row.item}>
            <TableCell>{row.item}</TableCell>
            <TableCell align="right">{row.qty}</TableCell>
        </TableRow>
    ));
    
    // Format order summary table data
    const summaryRows = [
        { label: "Total Orders (Completed + Void)", value: orderSummary.total },
        { label: "Completed", value: orderSummary.completed },
        { label: "Voided", value: orderSummary.voided },
        { label: "Dine In", value: orderSummary.dineIn },
        { label: "Take Out", value: orderSummary.takeOut },
        { label: "Kiosk", value: orderSummary.kiosk },
        { label: "Online", value: orderSummary.online },
    ];


    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            {/* 1. Order Timeline */}
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders by Hour
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="orders" stroke={theme.palette.primary.main} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>


            <Grid container spacing={3}>
                {/* 2. Order Summary */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#fff3e0" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableBody>
                                    {summaryRows.map(row => (
                                        <TableRow key={row.label}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="right">{row.value}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* 3. Top Items */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#e3f2fd" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Top Selling Items
                        </Typography>
                        {topItems.length > 0 ? (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Quantity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topItems.map(row => (
                                            <TableRow key={row.item}>
                                                <TableCell>{row.item}</TableCell>
                                                <TableCell align="right">{row.qty}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                         ) : (
                             <Typography sx={{ textAlign: 'center', py: 4 }}>No item data available.</Typography>
                         )}
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, my: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders Details
                </Typography>

                {/* Ordersdetails needs access to the combined list of completed orders */}
                <Ordersdetails orders={allCompletedOrders} showPagination={true}></Ordersdetails>

            </Paper>
        </Container>
    );
});